import { captureException, addBreadcrumb } from '@sentry/browser';

import { platformsEventsNameMap } from './config';

import { AnalyticsPlatform, EventsPayload, EventTriggerNames, PlatformName } from './types';
import { GTMPlatform } from './platforms';

type Mode = 'development' | 'production';

export class AnalyticsService {
  private static instance: AnalyticsService;

  private readonly platforms: Record<PlatformName, AnalyticsPlatform | undefined>;
  private mode: Mode = 'production';

  private constructor() {
    this.platforms = {
      GTM: undefined,
    };
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  public registerPlatform(platformName: PlatformName, platform: AnalyticsPlatform) {
    this.platforms[platformName] = platform;
  }

  public switchMode = (mode: Mode) => {
    this.mode = mode;
  };

  public emitEvent<T extends EventTriggerNames>(
    eventTriggerName: T,
    eventPayload: EventsPayload[T],
    onEventSent?: () => void,
  ) {
    for (const [platformKey, payloadToPlatform] of Object.entries(eventPayload)) {
      const platformName = platformKey as PlatformName;
      const platform = this.platforms[platformName];

      if (!platform) {
        if (this.mode === 'development') {
          console.groupCollapsed(
            `%c🚫 Platform %c${platformName} %cis not registered.`,
            'color: red; font-weight: bold;',
            'color: orange; font-weight: bold;',
            'color: inherit;',
          );
          console.trace();
          console.groupEnd();
        }
        captureException(`Platform ${platformName} is not registered. Event: ${eventTriggerName}`);
        return;
      } else {
        // Breadcrumb для контекста при ошибках (не создаёт отдельное событие)
        addBreadcrumb({
          message: `Analytics: ${eventTriggerName}`,
          category: 'analytics',
          level: 'info',
          data: { platform: platformName },
        });
        if (this.mode === 'development') {
          const emoji = platformName === 'GTM' ? '📦' : '📊';

          // Проверяем, есть ли в имени события слово 'error' (независимо от регистра)
          const eventNamesMap = platformsEventsNameMap[platformName];
          const eventName = eventNamesMap[eventTriggerName];
          const isErrorEvent = /error/i.test(eventName); // (игнорируем регистр)

          console.info(
            `%c${emoji} Event sent to platform:%c ${platformName} %c| %cEvent: "${eventName}"`,
            // Общий стиль для всей строки
            'background-color: #2ee84445; color: white; padding: 2px 2px; font-weight: bold; font-size: 14px; border-radius: 6px;',

            // Стиль для имени платформы (выделено отдельным цветом)
            'color: orange; font-weight: bold;',

            // Для разделителя " | "
            'color: inherit;',

            // Стиль для события (если это ошибка, выделяем красным)
            `color: ${isErrorEvent ? 'red' : 'green'}; font-weight: bold; white-space: pre-line;`,
          );

          console.groupCollapsed(
            `%c🔍 Payload details for 👆`,
            'color: white; font-weight: bold; background-color: teal; padding: 2px 2px; font-size: 14px; border-radius: 6px',
          );

          console.info(
            JSON.stringify(
              payloadToPlatform,
              (key, value) => {
                if (typeof value === 'object' && value !== null) {
                  return JSON.parse(JSON.stringify(value));
                }
                return value;
              },
              2,
            ),
          );

          // Выводим объект для JS (скрытый)
          console.groupCollapsed(
            '%c🕵️‍♂️ JS detail:',
            'color: teal; font-style: italic; font-weight: bold;',
          );
          console.dir(payloadToPlatform, { depth: 3 });
          console.groupEnd();

          console.groupEnd();
        }
        platform.sendEvent(
          platformsEventsNameMap[platformName][eventTriggerName],
          payloadToPlatform,
          onEventSent,
        );
      }
    }
  }
}

const analytics = AnalyticsService.getInstance();

const gtmInstance = new GTMPlatform();

analytics.registerPlatform(PlatformName.GTM, gtmInstance);

export { analytics, gtmInstance };
